<template>
  <div>
    <h1>Manage Specialists</h1>
  
    <b-button variant="primary" style="margin-bottom:20px;" v-b-modal.modal-crud>New Specialist</b-button>

    <b-modal id="modal-crud" hide-header hide-footer>
      <h2>{{formTitle}}</h2>
      <hr/>
      <form ref="form" @submit.prevent="save">
        <b-form-group
            :state="$v.editedItem.specialistType.$dirty ? !$v.editedItem.specialistType.$error : null"
            label="Specialist Type"
            label-for="specialist-type-select"
            invalid-feedback="Specialist Type is required">
          <b-form-select id="specialist-type-select" v-model="editedItem.specialistType"
                         :state="$v.editedItem.specialistType.$dirty ? !$v.editedItem.specialistType.$error : null"
                         :disabled="editedIndex  !== -1"
                         :readonly="editedIndex  !== -1"
                         :aria-disabled="editedIndex  !== -1"
                         :aria-readonly="editedIndex  !== -1"
                         :options="specialistTypeOptions">
            <template v-slot:first>
              <option :value="null" disabled>-- Please select an option --</option>
            </template>
          </b-form-select>

        </b-form-group>

          <b-form-group 
            label="User ID" label-for="userid-input"
            invalid-feedback="User Id is required">
            <b-form-input id="userid-input"
              v-model="$v.editedItem.userId.$model"
              :state="$v.editedItem.userId.$dirty ? !$v.editedItem.userId.$error : null"
              :disabled="editedIndex  !== -1"
              :readonly="editedIndex  !== -1"
              :aria-disabled="editedIndex  !== -1"
              :aria-readonly="editedIndex  !== -1"
              aria-describedby="input-2-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-2-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>     
            <b-form-text id="userid-input-help">Required.</b-form-text>
          </b-form-group>

          <b-form-group 
            label="Specialist Name" label-for="name-input"
            invalid-feedback="Specialist Name is required">
            <b-form-input id="name-input" 
              v-model="$v.editedItem.name.$model" 
              :state="$v.editedItem.name.$dirty ? !$v.editedItem.name.$error : null"
              aria-describedby="input-1-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-1-live-feedback">
              This is a required field.
            </b-form-invalid-feedback>     
            <b-form-text id="name-input-help">Required.</b-form-text> 
          </b-form-group>

        <b-form-group
                label="Email Address" label-for="email-input"
                invalid-feedback="Email Address is required">
          <b-form-input id="name-input"
                        v-model="$v.editedItem.email.$model"
                        :state="$v.editedItem.email.$dirty ? !$v.editedItem.email.$error : null"
                        aria-describedby="input-3-live-feedback"
                        required>
          </b-form-input>
          <b-form-invalid-feedback id="input-3-live-feedback">
            This is a required field and must be a valid email address.
          </b-form-invalid-feedback>
          <b-form-text id="email-input-help">Required.</b-form-text>
        </b-form-group>

        <b-row style="margin-bottom:20px;">
          <b-col>
            <b-form-checkbox v-model="$v.editedItem.active.$model" name="active-switch" data-test="active-switch" switch>
                <b>Active?</b>
            </b-form-checkbox>
          </b-col>
        </b-row>    

          <b-button @click="close" style="margin-right:15px;">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="$v.editedItem.$invalid">Save</b-button>
        </form>
    </b-modal>

    <b-row>
      <b-col style="text-align: right; margin-right: 20px;">
        <b-form-checkbox v-model="showInactive" name="showinactive-filter" data-test="showinactive-filter" switch>
            <b>Include inactive staff specialists?</b>
        </b-form-checkbox>
      </b-col>
    </b-row>

    <b-table hover small responsive show-empty
      :fields="fields"
      :items="specialists"
      :busy="isLoading">
      <template v-slot:cell(actions)="row">
        <b-button size="sm" @click="editItem(row.item)" class="mr-1">
          edit
        </b-button>
      </template>
      <template #table-caption></template>
      <template v-slot:cell(active)="data">
        {{ data.item.active ? 'Yes' : 'No' }}
      </template>
      
      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Reset</v-btn>
      </template>

      <template v-slot:empty="scope">
        <p>No specialists found.</p>
      </template>

      <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle" style="margin-right:5px; margin-top:12px;"></b-spinner>
          <strong>Loading, Please Wait...</strong>
      </div>        

    </b-table>
  </div>
</template>

<script>
  import axios from 'axios';
  import axiosRetry from 'axios-retry';
  import { validationMixin } from 'vuelidate'
  import pageTitle from '@/mixins/pageTitle'
  import { required, email, maxLength } from 'vuelidate/lib/validators'
  import isUserIdUnique from '@/validators/isUserIdUnique.js'
  import {commonOptions} from "@/mixins/commonOptions";

  axiosRetry(axios, { retries: 3,
                      retryDelay: axiosRetry.exponentialDelay  });

  export default {
    title: 'Manage Specialists',
    mixins: [validationMixin,commonOptions,pageTitle],
    data: () => ({
      fields: [
        {key: 'specialistType', sortable: true, label: 'Specialist Type'},
        {key: 'userId', sortable: true, label: 'User ID'},
        {key: 'name', sortable: true, label: 'Specialist Name'},
        {key: 'email', sortable: true, label: 'Email Address'},
        {key: 'active', sortable: true, label: 'Active?'},
        {key: 'actions', label: 'Actions'}
      ],
      isLoading: false,
      showInactive: false,
      specialists: [],
      editedIndex: -1,
      editedItem: {
        specialistType: null,
        userId: null,
        name: null,
        active: true,
        email: null,
      },
      defaultItem: {
        specialistType: null,
        userId: null,
        name: null,
        active: true,
        email: null,
      },
    }),

    validations: {
      editedItem: {
        specialistType:{
          required
        },
        userId: {
          required
        },
        name: {
          required
        },
        email: {
          required,
          email
        },
        active: {},
      }
    },

    computed: {
      formTitle () {
        return this.editedIndex === -1 ? 'New Specialist' : 'Edit Specialist';
      }
    },

    watch: {
      specialists() {
        this.isLoading = false;
      },
      showInactive() {
        this.fetchSpecialists()
      }
    },

    created () {
      this.initialize();
    },

    methods: {
      fetchSpecialists() {
          this.isLoading = true;
          let staffingUrl = `${process.env.VUE_APP_API_BASE_URL}/organization/specialist`
          let payrollUrl = `${process.env.VUE_APP_API_BASE_URL}/organization/payrollspecialist`
          let delegateUrl = `${process.env.VUE_APP_API_BASE_URL}/organization/delegate`
          if (this.showInactive) {
            staffingUrl = staffingUrl + '/all'
            payrollUrl = payrollUrl + '/all'
            delegateUrl = delegateUrl + '/all'

          } 
          axios.all([
              axios.get(staffingUrl),
              axios.get(delegateUrl),
              axios.get(payrollUrl)]
          )
            .then(responseArr => {
              let staffing = responseArr[0].data.map(o => ({...o, specialistType: 'Staffing'}))
              let payroll = responseArr[2].data.map(o => ({...o, specialistType: 'Payroll'}))
              let delegate = responseArr[1].data.map(o => ({...o, specialistType: 'Delegate'}))
              this.specialists = staffing;
              this.specialists.push(...payroll);
              this.specialists.push(...delegate);
              this.isLoading = false;
            });
      },

      initialize () {
        this.fetchSpecialists();
      },

      editItem (item) {
        this.editedIndex = this.specialists.indexOf(item);
        this.editedItem = Object.assign({}, item);
        this.$bvModal.show('modal-crud');
      },

      deleteItem (item) {
        const index = this.specialists.indexOf(item);
        confirm('Are you sure you want to delete this item?') && this.specialists.splice(index, 1);
      },

      close () {
        this.$bvModal.hide('modal-crud');
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem);
          this.editedIndex = -1;
          this.fetchSpecialists();
        }, 300)
      },

      save () {
        let specialistType = 'specialist';
        if (this.editedItem.specialistType === 'Payroll') {
          specialistType = 'payrollspecialist';
        }
        if (this.editedItem.specialistType === 'Delegate') {
          specialistType = 'delegate';
        }
        if (this.editedIndex > -1) {
            axios.put(`${process.env.VUE_APP_API_BASE_URL}/organization/`+specialistType, this.editedItem)
            .then(response => { 
              this.fetchSpecialists();
                this.$bvToast.toast('Specialist Updated', {
                  title: 'Status',
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: 'success',
                  appendToast: true
                });
            });
          
        } else {
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/organization/`+specialistType, this.editedItem)
            .then(response => { 
              this.fetchSpecialists();
                this.$bvToast.toast('Specialist Saved', {
                  title: 'Status',
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: 'success',
                  appendToast: true
                });
            });
          
        }
        this.close();
      },
    }
  }
</script>